/**
 * Per-location feeding-dashboard settings: chart reference targets plus the
 * green/yellow/red colour boundaries. Percentages are in their natural unit
 * (5 = 5 %). Backend seeds every field with FEEDING_CONFIG_DEFAULTS on first
 * access, so this is always fully populated.
 */
export interface FeedingDashboardConfig {
    /** FCR bio target — reference line and the green/yellow boundary. */
    fcrTarget: number;
    /** FCR caution margin (%) above target: yellow up to target×(1+pct/100), red beyond. */
    fcrWarnPct: number;
    /** RGI target — reference line and the green/yellow boundary. */
    rgiTarget: number;
    /** RGI caution margin (%) below target: yellow down to target×(1−pct/100), red below. */
    rgiWarnPct: number;
    /** @deprecated No Bioscope-vs-PMS deviation column exists in the spec. */
    weightDevGoodPct: number;
    /** @deprecated See weightDevGoodPct. */
    weightDevWarnPct: number;
    /** @deprecated Feed vs plan is coloured by sign per spec, not by magnitude. */
    feedVsPlanGoodPct: number;
    /** @deprecated See feedVsPlanGoodPct. */
    feedVsPlanWarnPct: number;
    /** FCR confidence interval green band: CI ≤ pct of the estimate is green. */
    confIntervalGoodPct: number;
    /** FCR confidence interval yellow band: CI ≤ pct of the estimate is yellow, wider is red. */
    confIntervalWarnPct: number;
    /** Fallthrough yellow threshold (%): at/above this is yellow. */
    fallthroughWarnPct: number;
    /** Fallthrough red threshold (%): above this is red. */
    fallthroughAlertPct: number;
    /** Camera depth (m) at or below which detected pellets count as fallthrough. */
    fallthroughDepthThresholdM: number;
    /**
     * SFR colour bands (%). Defaults from the Pen Comparison spec: "Healthy range for
     * salmon: 1-2%. Above ~3% may mean overfeeding."
     */
    sfrHealthyMinPct: number;
    sfrHealthyMaxPct: number;
    sfrOverfeedPct: number;
    /** Bioscope measurement days before estimates count as reliable (IPS req 7). */
    bioscopeReadyDays: number;
    /** Period length (days) above which the series buckets weekly (IPS req 4). */
    weeklyResolutionDays: number;
    /**
     * The customer's production-planning system, shown wherever a label would otherwise
     * read generically (IPS req 28). Null renders the generic wording.
     */
    pmsSystemName: string | null;
}

/**
 * Code defaults, mirroring the backend schema column defaults. Used to prefill the
 * settings form's "Reset to defaults" action; the live config from the API is the
 * source of truth for rendering.
 */
export const FEEDING_CONFIG_DEFAULTS: FeedingDashboardConfig = {
    fcrTarget: 1.12,
    fcrWarnPct: 5,
    rgiTarget: 100,
    rgiWarnPct: 10,
    weightDevGoodPct: 5,
    weightDevWarnPct: 10,
    feedVsPlanGoodPct: 5,
    feedVsPlanWarnPct: 15,
    confIntervalGoodPct: 25,
    confIntervalWarnPct: 40,
    fallthroughWarnPct: 8,
    fallthroughAlertPct: 10,
    fallthroughDepthThresholdM: 20,
    sfrHealthyMinPct: 1,
    sfrHealthyMaxPct: 2,
    sfrOverfeedPct: 3,
    bioscopeReadyDays: 21,
    weeklyResolutionDays: 60,
    pmsSystemName: null,
};

export interface UpdateFeedingDashboardConfig {
    fcrTarget?: number;
    fcrWarnPct?: number;
    rgiTarget?: number;
    rgiWarnPct?: number;
    weightDevGoodPct?: number;
    weightDevWarnPct?: number;
    feedVsPlanGoodPct?: number;
    feedVsPlanWarnPct?: number;
    confIntervalGoodPct?: number;
    confIntervalWarnPct?: number;
    fallthroughWarnPct?: number;
    fallthroughAlertPct?: number;
    fallthroughDepthThresholdM?: number;
    sfrHealthyMinPct?: number;
    sfrHealthyMaxPct?: number;
    sfrOverfeedPct?: number;
    bioscopeReadyDays?: number;
    weeklyResolutionDays?: number;
    pmsSystemName?: string | null;
}
